import { useState } from "react";

import { CaseDiagnosis, CptCode, CptInput } from "custom-models";

import ModifyDiagnosisView from "./ModifyDiagnosisView";
import { cptString, reduceCptCodes } from "../DiagnosisDialogHelpers";

type AddendDiagnosisDialogProps = {
  hat: string;
  completeDiagnosis: CaseDiagnosis;
  addendDiagnosis: CaseDiagnosis;
  onUpdateDiagnosis: Function;
  isModMedCase?: boolean;
};

/*
 * Get the difference in cpt codes from the most current complete diagnosis and the incomplete addend diagnosis
 * While comparing:
 *   If the addend cpt code does not exist in the complete diagnosis list, add the cpt code to the return list
 *   If the addend cpt code does exist in the complete diagnosis list but has higher quantity,
 *     Add the code with the difference in quantity to the return list
 */

const getAddendCptCodes = (
  prevCodes: CptInput[],
  addendCodes: CptInput[]
): CptInput[] => {
  const prevCodesObj = prevCodes.reduce(
    (acc: { [key: string]: CptInput }, cptInput) => {
      return { ...acc, [cptString(cptInput.code)]: cptInput };
    },
    {}
  );

  return addendCodes.reduce((acc: CptInput[], cptInput) => {
    const codeStr = cptString(cptInput.code);
    if (!prevCodesObj[codeStr]) {
      acc.push(cptInput);
    } else if (
      (prevCodesObj[codeStr].quantity || 0) < (cptInput.quantity || 0)
    ) {
      acc.push({
        ...cptInput,
        quantity:
          (cptInput.quantity || 0) - (prevCodesObj[codeStr].quantity || 0),
      });
    }
    return acc;
  }, []);
};

const AddendDiagnosisDialog = ({
  hat,
  completeDiagnosis,
  addendDiagnosis,
  onUpdateDiagnosis,
  isModMedCase,
}: AddendDiagnosisDialogProps) => {
  const [addendForm, setAddendForm] = useState<{ [key: string]: string }>({
    diagnosis:
      addendDiagnosis?.diagnosis
        .replace(completeDiagnosis.diagnosis || "", "")
        ?.trim() || "",
    microscopic:
      addendDiagnosis?.microscopic
        ?.replace(completeDiagnosis.microscopic || "", "")
        ?.trim() || "",
    comment:
      addendDiagnosis?.comment
        ?.replace(completeDiagnosis.comment || "", "")
        ?.trim() || "",
  });

  const [addendCpt, setAddendCpt] = useState<CptInput[]>(
    getAddendCptCodes(
      reduceCptCodes(completeDiagnosis.cpt_codes || []),
      reduceCptCodes(addendDiagnosis.cpt_codes || [])
    )
  );

  const updateDiagnosis = (field: string, value: string) => {
    const updatedValue =
      completeDiagnosis[field as keyof CaseDiagnosis] + `\n${value}`;
    setAddendForm({
      ...addendForm,
      [field]: value,
    });
    onUpdateDiagnosis(field, updatedValue);
  };

  const updateDiagnosisCpt = (codes: CptCode[]) => {
    const updatedCodes = (completeDiagnosis.cpt_codes || []).concat(codes);
    onUpdateDiagnosis("cpt_codes", updatedCodes);
  };

  return (
    <ModifyDiagnosisView
      hat={hat}
      caseId={addendDiagnosis.case}
      addonDiagnosis={addendForm}
      completeDiagnosis={completeDiagnosis}
      onUpdateDiagnosis={updateDiagnosis}
      onUpdateDiagnosisCpt={updateDiagnosisCpt}
      cptCodes={addendCpt}
      setCptCodes={setAddendCpt}
      displayFlatDiagnosis
      isModMedCase={isModMedCase}
    />
  );
};

export default AddendDiagnosisDialog;
